import { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import {
  Plus, MagnifyingGlass, QrCode, PencilSimple, Trash, Package, Sparkle, Handshake, Warning, Image as ImageIcon, DotsThreeVertical,
} from '@phosphor-icons/react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { EmptyState } from '@/components/enterprise/EmptyState';
import { bihinApi, fileUrl, STATUS_META } from './bihinApi';
import AiAssetImportModal from '@/components/aiimport/AiAssetImportModal';
import BihinItemFormDialog from './BihinItemFormDialog';
import BihinItemDetailDialog from './BihinItemDetailDialog';
import BihinQrLabelDialog from './BihinQrLabelDialog';

const KIND_TABS = [
  { key: 'all', label: 'すべて' },
  { key: 'equipment', label: '備品' },
  { key: 'consumable', label: '消耗品' },
];

export default function BihinItems() {
  const { access } = useOutletContext();
  const canEdit = !!access?.can_edit;
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('all');
  const [status, setStatus] = useState('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [aiImportOpen, setAiImportOpen] = useState(false);
  const [qrItem, setQrItem] = useState(null);

  const [totalCount, setTotalCount] = useState(null);
  const filtered = kind !== 'all' || status !== 'all' || !!q.trim();

  const load = useCallback(() => {
    bihinApi.items({ kind, status, q: q || undefined }).then(setItems).catch(() => {});
    // 絞り込みで0件なのか、1件も登録が無いのかを区別するため総数も見る
    bihinApi.items({}).then((all) => setTotalCount(all.length)).catch(() => {});
  }, [kind, status, q]);
  useEffect(() => { load(); }, [load]);

  const clearFilters = () => { setKind('all'); setStatus('all'); setQ(''); };
  useEffect(() => { bihinApi.departments().then(setDepartments).catch(() => {}); }, []);

  // QRスキャン deep-link: /bihin/items?open=<id>
  useEffect(() => {
    const open = searchParams.get('open');
    if (open) {
      setDetailId(open);
      searchParams.delete('open');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const deptName = useMemo(() => {
    const m = {};
    for (const d of departments) m[d.id] = d.name;
    return m;
  }, [departments]);

  // 所属部門ごとにグループ化
  const groups = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const key = it.department_id && deptName[it.department_id] ? deptName[it.department_id] : '未割当';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    }
    return [...map.entries()]
      .map(([name, list]) => ({ name, list }))
      .sort((a, b) => (a.name === '未割当' ? 1 : b.name === '未割当' ? -1 : a.name.localeCompare(b.name, 'ja')));
  }, [items, deptName]);

  const del = async (it) => {
    if (!window.confirm(`「${it.name}」を削除しますか？貸出履歴も削除されます。`)) return;
    try {
      await bihinApi.deleteItem(it.id);
      toast.success('削除しました');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '削除に失敗しました');
    }
  };

  return (
    <div className="space-y-4" data-testid="bihin-items-page">
      <PageHeader
        testid="bihin-items-header"
        title={`備品一覧（${items.length}件）`}
        description="所属部門ごとに表示しています。カードをクリックすると詳細・貸出ができます。"
        secondary={canEdit ? (
          <Button variant="outline" onClick={() => setAiImportOpen(true)} data-testid="bihin-ai-import-btn">
            <Sparkle size={15} weight="fill" /><span className="ml-1">AIインポート</span>
          </Button>
        ) : null}
        primary={canEdit ? (
          <Button onClick={() => { setEditItem(null); setFormOpen(true); }} data-testid="bihin-new-item-btn">
            <Plus size={15} /><span className="ml-1">備品を登録</span>
          </Button>
        ) : null}
      />

      {/* filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex gap-1" data-testid="bihin-kind-tabs">
          {KIND_TABS.map((t) => (
            <button key={t.key} type="button" onClick={() => setKind(t.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                kind === t.key ? 'bg-teal-700 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
              }`}
              data-testid={`bihin-kind-tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-32 h-9" data-testid="bihin-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">全ステータス</SelectItem>
            {Object.entries(STATUS_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-48 max-w-xs">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="名前・品番・メーカーで検索…" className="pl-9 h-9" data-testid="bihin-search" />
        </div>
      </div>

      {items.length === 0 ? (
        (filtered && totalCount !== 0) ? (
          <EmptyState
            testid="bihin-items-empty-filtered"
            icon={MagnifyingGlass}
            title="条件に一致する備品はありません"
            action={(
              <Button variant="outline" onClick={clearFilters} data-testid="bihin-clear-filters">
                絞り込みを解除
              </Button>
            )}
          />
        ) : (
          <EmptyState
            testid="bihin-items-empty"
            icon={Package}
            title="備品がまだ登録されていません"
            action={canEdit ? (
              <Button onClick={() => { setEditItem(null); setFormOpen(true); }} data-testid="bihin-empty-cta">
                <Plus size={14} className="mr-1" /> 最初の備品を登録
              </Button>
            ) : null}
          />
        )
      ) : (
        groups.map((g) => (
          <div key={g.name} data-testid={`bihin-dept-group-${g.name}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-bold text-zinc-700">{g.name}</span>
              <Badge variant="outline" className="bg-zinc-100 text-zinc-600 border-zinc-200 text-[10px] px-1.5 py-0">{g.list.length}</Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2.5">
              {g.list.map((it) => {
                const st = STATUS_META[it.status] || STATUS_META.storage;
                return (
                  <div key={it.id}
                    className="rounded-lg border border-zinc-200 bg-white p-3 hover:border-teal-500 hover:shadow-sm transition-[border-color,box-shadow] cursor-pointer"
                    onClick={() => setDetailId(it.id)}
                    data-testid={`bihin-item-card-${it.id}`}>
                    <div className="flex gap-3">
                      <div className="shrink-0 w-14 h-14 rounded-lg bg-zinc-100 overflow-hidden flex items-center justify-center">
                        {it.photo_url && !it.photo_is_pdf ? (
                          <img src={fileUrl(it.photo_url)} alt={it.name} className="w-full h-full object-cover" />
                        ) : (
                          <ImageIcon size={20} className="text-zinc-300" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-zinc-800 truncate" title={it.name} data-no-translate>{it.name}</div>
                        <div className="text-[10px] font-mono text-zinc-400 truncate" title={it.asset_no || ''} data-no-translate>{it.asset_no || '—'}</div>
                        <div className="mt-1 flex items-center gap-1 flex-wrap">
                          {it.kind === 'equipment' ? (
                            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${st.cls}`}>{st.label}</Badge>
                          ) : (
                            <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${it.low_stock ? 'bg-rose-50 text-rose-600 border-rose-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
                              在庫 {it.stock_qty ?? 0}{it.unit || ''}
                            </Badge>
                          )}
                          {it.kind === 'consumable' && it.pack_label && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-zinc-50 text-zinc-500 border-zinc-200"
                              data-testid={`bihin-item-pack-${it.id}`}>
                              {it.pack_label}
                            </Badge>
                          )}
                          {it.active_loan && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-blue-50 text-blue-700 border-blue-200">
                              <Handshake size={9} className="mr-0.5" />{it.active_loan.borrower_name}
                            </Badge>
                          )}
                          {it.inspection_state === 'overdue' && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-rose-50 text-rose-600 border-rose-200">
                              <Warning size={9} className="mr-0.5" />点検期限切れ
                            </Badge>
                          )}
                          {it.inspection_state === 'due_soon' && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200">点検間近</Badge>
                          )}
                        </div>
                      </div>
                      <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" aria-label="操作" data-testid={`bihin-item-menu-${it.id}`}>
                              <DotsThreeVertical size={16} weight="bold" className="text-zinc-500" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white">
                            <DropdownMenuItem
                              onClick={() => setQrItem({ id: it.id, no: it.asset_no, name: it.name, sub: it.location })}
                              data-testid={`bihin-item-qr-${it.id}`} className="gap-2">
                              <QrCode size={14} /> QRラベル
                            </DropdownMenuItem>
                            {canEdit && (
                              <>
                                <DropdownMenuItem onClick={() => { setEditItem(it); setFormOpen(true); }} data-testid={`bihin-item-edit-${it.id}`} className="gap-2">
                                  <PencilSimple size={14} /> 編集
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => del(it)} data-testid={`bihin-item-del-${it.id}`} className="gap-2 text-rose-600 focus:text-rose-600">
                                  <Trash size={14} /> 削除
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      <BihinItemFormDialog
        open={formOpen} onOpenChange={setFormOpen}
        item={editItem} departments={departments}
        onSaved={() => { setFormOpen(false); load(); }}
      />
      <BihinItemDetailDialog
        open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)}
        itemId={detailId} canEdit={canEdit} deptName={deptName}
        onChanged={load}
        onEdit={(it) => { setDetailId(null); setEditItem(it); setFormOpen(true); }}
      />
      <BihinQrLabelDialog open={!!qrItem} onOpenChange={(o) => !o && setQrItem(null)} item={qrItem} />
      <AiAssetImportModal module="bihin" open={aiImportOpen} onClose={() => setAiImportOpen(false)} onImported={load} />
    </div>
  );
}
